import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  updateProfile,
  type UserCredential,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase/config";
import { getAuthErrorMessage } from "../lib/firebaseErrors";
import { createLogger, maskEmail } from "../lib/logger";
import { TERMS_META } from "../data/TermsContent";

const log = createLogger("AuthService");

// ─── Result type ────────────────────────────────────────────────────────────

export type AuthResult<T = void> =
  | { ok: true; data: T }
  | { ok: false; error: string };

// ─── Sign In ────────────────────────────────────────────────────────────────

export async function signIn(email: string, password: string): Promise<AuthResult<UserCredential>> {
  try {
    log.info("Sign-in attempt", { email: maskEmail(email) });
    const cred = await signInWithEmailAndPassword(auth, email, password);
    log.info("Sign-in successful", { uid: cred.user.uid, emailVerified: cred.user.emailVerified });

    // Force-reload the user profile so emailVerified reflects the latest
    // server state. Without this, a user who just clicked the verification
    // link gets a stale token that still says emailVerified: false.
    await cred.user.reload();
    const refreshedUser = auth.currentUser;
    log.info("User reloaded", { uid: refreshedUser?.uid, emailVerified: refreshedUser?.emailVerified });

    if (refreshedUser?.emailVerified) {
      await refreshedUser.getIdToken(true);
      await ensureUserDocOnLogin(cred);
    }

    return { ok: true, data: cred };
  } catch (err) {
    const message = getAuthErrorMessage(err, "signIn");
    return { ok: false, error: message };
  }
}

async function ensureUserDocOnLogin(cred: UserCredential) {
  const { user } = cred;
  try {
    const userDocRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) return;

    await setDoc(userDocRef, {
      fullName: user.displayName || null,
      email: user.email,
      ftuCompleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    log.info("User doc recreated from auth profile (safety-net)", { uid: user.uid });
  } catch (err) {
    log.error("ensureUserDocOnLogin failed", {
      uid: user.uid,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

// ─── Sign Up ────────────────────────────────────────────────────────────────

export interface SignUpPayload {
  firstName: string;
  lastName: string;
  dob: Date;
  email: string;
  password: string;
  agreeTerms: boolean;
  agreeAnonymisedData: boolean;
  agreeAgeAndBeta: boolean;
  agreeMarketing: boolean;
  continueUrl: string;
}

export async function signUp(payload: SignUpPayload): Promise<AuthResult<UserCredential>> {
 const { firstName, lastName, dob, email, password, agreeAnonymisedData, agreeAgeAndBeta, agreeMarketing, continueUrl } = payload;
  const fullName = `${firstName} ${lastName}`.trim();

  try {
    log.info("Sign-up attempt", { email: maskEmail(email) });

    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const { user } = cred;
    log.info("Auth user created", { uid: user.uid });
    await user.getIdToken(true);
    try {
      await updateProfile(user, { displayName: fullName || undefined });
    } catch (err) {
      log.warn("updateProfile failed (non-blocking)", {
        uid: user.uid,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    await setDoc(
      doc(db, "users", user.uid),
      {
        firstName: firstName || null,
        lastName: lastName || null,
        fullName: fullName || null,
        birthDate: dob.toISOString(),
        email: user.email || email,
        termsAccepted: {
          version: TERMS_META.version,
          acceptedAt: serverTimestamp(),
        },
       agreeAnonymisedData,
        agreeAgeAndBeta,
        agreeMarketing,
        ftuCompleted: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    log.info("User doc written", { uid: user.uid });

    try {
      await sendEmailVerification(user, {
        url: continueUrl,
        handleCodeInApp: false,
      });
      log.info("Verification email sent", { uid: user.uid });
    } catch (err) {
      log.warn("sendEmailVerification failed (non-blocking)", {
        uid: user.uid,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    // signOut removed — user stays signed in so VerifyEmail polling
    // can detect when they click the verification link.
    // Unverified users are already blocked from all protected routes by App.tsx.

    return { ok: true, data: cred };
  } catch (err) {
    const message = getAuthErrorMessage(err, "signUp");
    return { ok: false, error: message };
  }
}

// ─── Password Reset ─────────────────────────────────────────────────────────

export async function resetPassword(email: string): Promise<AuthResult> {
  try {
    log.info("Password reset requested", { email: maskEmail(email) });
    await sendPasswordResetEmail(auth, email);
    log.info("Password reset email sent", { email: maskEmail(email) });
    return { ok: true, data: undefined };
  } catch (err) {
    const message = getAuthErrorMessage(err, "resetPassword");
    return { ok: false, error: message };
  }
}

// ─── Resend Verification ────────────────────────────────────────────────────

export async function resendVerification(continueUrl?: string): Promise<AuthResult> {
  try {
    const user = auth.currentUser;
    if (!user) {
      log.warn("resendVerification called with no current user");
      return { ok: false, error: "No signed-in user. Please log in again." };
    }

    const actionCodeSettings = continueUrl
      ? { url: continueUrl, handleCodeInApp: false }
      : undefined;

    await sendEmailVerification(user, actionCodeSettings);
    log.info("Verification email resent", { uid: user.uid });
    return { ok: true, data: undefined };
  } catch (err) {
    const message = getAuthErrorMessage(err, "resendVerification");
    return { ok: false, error: message };
  }
}

// ─── Sign Out ───────────────────────────────────────────────────────────────

export async function logOut(): Promise<AuthResult> {
  try {
    const user = auth.currentUser;
    const uid = user?.uid;

    if (uid) {
      try {
        const userDocRef = doc(db, "users", uid);
        const userSnap = await getDoc(userDocRef);

        if (userSnap.exists()) {
          const data = userSnap.data();

          if (data.ftuCompleted === true) {
            await updateDoc(userDocRef, {
              hasVisitedDashboard: true,
              updatedAt: serverTimestamp(),
            });
          }
        }
      } catch (dbErr) {
        log.error("Firestore update failed during logout", { error: dbErr });
      }

      sessionStorage.removeItem(`bodyown_greeting_lock_${uid}`);
    }

    await signOut(auth);
    log.info("User signed out", { uid });

    return { ok: true, data: undefined };
  } catch (err) {
    log.error("logOut failure", {
      error: err instanceof Error ? err.message : String(err),
    });

    await signOut(auth).catch(() => {});
    const message = getAuthErrorMessage(err, "logOut");
    return { ok: false, error: message };
  }
}